// Le tirage des équipes (D-175 lot 1, D-176 ; maquettes t1 à t3). Logique pure, hasard injecté par une graine.
//  * La fiche « Pour les tirages » : un niveau sur une seule échelle à 3 crans (Découverte, À l'aise, Confirmé ; 1 à 3
//    étoiles), un ou deux postes (Meneur, Extérieur, Intérieur, Polyvalent ; proposés à partir des U13), fille ou garçon dans
//    un groupe mixte. Le staff seul la voit ; une valeur par joueuse et par équipe de la saison, effacée en fin de saison.
//  * Le tirage : les présentes seulement ; un nombre d'équipes (ou de joueuses par équipe) ; niveau équilibré (serpentin au
//    hasard dans un même niveau) ou ignoré ; postes ventilés ou ignorés ; catégorie mélangée, regroupée par âge, ou au moins
//    une plus jeune par équipe ; mixité (groupe mixte) ; liens « ensemble » / « séparer ». Une joueuse sans niveau compte
//    « À l'aise » et elle est signalée ; un niveau peut se donner pour ce tirage seulement. « Relancer » : un autre tirage
//    aussi juste. Tout est déterministe pour une même graine.
import { BIBS, type Bib, type ExoGroup } from './groups';
import { agree, type TeamGender } from './labels';

export type Level = 1 | 2 | 3;
export type Poste = 'M' | 'E' | 'I' | 'P';
export type Sex = 'F' | 'M';
export const LEVELS: Level[] = [1, 2, 3];
export const POSTES: Poste[] = ['M', 'E', 'I', 'P'];
/** Une joueuse sans niveau compte « À l'aise » (D-176, point 5). */
export const DEFAULT_LEVEL: Level = 2;
export const TEAMS_MIN = 2;
export const TEAMS_MAX = BIBS.length;

export const levelLabel = (l: Level, g: TeamGender): string => (l === 1 ? 'Découverte' : l === 2 ? 'À l’aise' : agree(g, 'Confirmé', 'Confirmée'));
export function posteLabel(p: Poste, g: TeamGender): string {
  return p === 'M' ? agree(g, 'Meneur', 'Meneuse') : p === 'E' ? agree(g, 'Extérieur', 'Extérieure')
    : p === 'I' ? agree(g, 'Intérieur', 'Intérieure') : agree(g, 'Polyvalent', 'Polyvalente');
}
/** Les postes se notent à partir des U13 (ignorés jusqu'en U11, D-175). */
export const postesProposed = (category: string | null | undefined): boolean => !['U7', 'U9', 'U11'].includes(category ?? '');
/** Fille ou garçon : seulement dans un groupe mixte. */
export const sexProposed = (gender: TeamGender): boolean => gender === 'X';

export const isLevel = (v: unknown): v is Level => v === 1 || v === 2 || v === 3;
const isPoste = (v: unknown): v is Poste => typeof v === 'string' && (POSTES as string[]).includes(v);

export interface DrawProfile { level: Level | null; positions: Poste[]; sex: Sex | null }
/** Une fiche relue sans confiance : un ou deux postes connus, dans l'ordre M, E, I, P. */
export function readProfile(v: unknown): DrawProfile {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const pos = Array.isArray(o.positions) ? [...new Set(o.positions.filter(isPoste))] : [];
  return {
    level: isLevel(o.level) ? o.level : null,
    positions: POSTES.filter(p => pos.includes(p)).slice(0, 2),
    sex: o.sex === 'F' || o.sex === 'M' ? o.sex : null,
  };
}
/** Toucher un poste : le retirer, ou l'ajouter (deux au plus : le premier choisi laisse sa place). */
export function togglePoste(cur: Poste[], p: Poste): Poste[] {
  if (cur.includes(p)) return cur.filter(x => x !== p);
  const next = [...cur, p].slice(-2);
  return POSTES.filter(x => next.includes(x));
}
export const emptyProfile = (p: DrawProfile) => p.level === null && !p.positions.length && p.sex === null;

// -----------------------------------------------------------------------------------------------------------------
// Le tirage
// -----------------------------------------------------------------------------------------------------------------
export interface DrawPlayer { id: string; level: Level | null; postes: Poste[]; sex: Sex | null; birthYear: number | null }
export type LevelRule = 'balance' | 'ignore';
export type PosteRule = 'spread' | 'ignore';
export type CategoryRule = 'mix' | 'age' | 'young';
export type MixRule = 'balance' | 'ignore';
export interface DrawCriteria {
  level: LevelRule;
  postes: PosteRule;
  category: CategoryRule;
  mixite: MixRule;
  /** Liens : deux prénoms à mettre ensemble, ou à séparer. */
  together: [string, string][];
  apart: [string, string][];
}
export const DEFAULT_CRITERIA: DrawCriteria = { level: 'balance', postes: 'spread', category: 'mix', mixite: 'balance', together: [], apart: [] };

/** Relus sans confiance (stockés sur l'appareil du coach). */
export function readCriteria(v: unknown): DrawCriteria {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const pairs = (x: unknown): [string, string][] => (Array.isArray(x) ? x : [])
    .filter((p): p is [string, string] => Array.isArray(p) && p.length === 2 && typeof p[0] === 'string' && typeof p[1] === 'string' && p[0] !== p[1])
    .slice(0, 20).map(p => [p[0], p[1]]);
  return {
    level: o.level === 'ignore' ? 'ignore' : 'balance',
    postes: o.postes === 'ignore' ? 'ignore' : 'spread',
    category: o.category === 'age' || o.category === 'young' ? o.category : 'mix',
    mixite: o.mixite === 'ignore' ? 'ignore' : 'balance',
    together: pairs(o.together),
    apart: pairs(o.apart),
  };
}
/** Un lien de plus entre deux personnes (le précédent entre elles est remplacé). */
export function setLink(c: DrawCriteria, a: string, b: string, kind: 'together' | 'apart' | null): DrawCriteria {
  const other = (p: [string, string]) => !((p[0] === a && p[1] === b) || (p[0] === b && p[1] === a));
  const together = c.together.filter(other), apart = c.apart.filter(other);
  if (kind === 'together' && a !== b) together.push([a, b]);
  if (kind === 'apart' && a !== b) apart.push([a, b]);
  return { ...c, together, apart };
}

/** Combien d'équipes : un nombre (2 à 6), ou « N par équipe » (autant d'équipes complètes que possible, les autres
 *  joueuses en plus ; deux équipes au moins). Jamais plus d'équipes que de joueuses. */
export function teamsFor(n: number, mode: 'count' | 'per', value: number, max = TEAMS_MAX): number {
  const k = mode === 'per' ? Math.floor(n / Math.max(1, value)) : value;
  return Math.max(1, Math.min(max, n, Math.max(TEAMS_MIN, k)));
}
/** Les tailles des équipes : à une près, les plus grandes d'abord. */
export function sizesFor(n: number, k: number): number[] {
  const base = Math.floor(n / Math.max(1, k)), extra = n % Math.max(1, k);
  return Array.from({ length: Math.max(1, k) }, (_, i) => base + (i < extra ? 1 : 0));
}

/** La saison FFBB finit en juin : l'année de fin de la saison en cours à cette date. */
export const seasonEndYear = (d: Date): number => d.getFullYear() + (d.getMonth() >= 6 ? 1 : 0);
/** « U12 » : la catégorie d'âge d'une année de naissance (année de fin de saison − année de naissance). */
export const ageLabel = (birthYear: number, endYear: number): string => `U${endYear - birthYear}`;
/** L'année des plus jeunes, s'il y a au moins deux années de naissance dans le groupe. */
export function youngestYear(players: readonly DrawPlayer[]): number | null {
  const years = [...new Set(players.map(p => p.birthYear).filter((y): y is number => typeof y === 'number'))];
  return years.length >= 2 ? Math.max(...years) : null;
}

export interface DrawRequest {
  players: DrawPlayer[];
  teams: number;
  criteria: DrawCriteria;
  /** Un niveau donné pour ce tirage seulement (oublié ensuite). */
  temp?: Record<string, Level>;
  seed: number;
  /** Le tirage d'avant (« Relancer ») : un autre, aussi juste, s'il en existe un. */
  avoid?: string[][] | null;
  /** Le nombre d'équipes au plus (par défaut : autant que de chasubles ; un tournoi en forme davantage, D-177). */
  max?: number;
}
export interface DrawTeam { bib: Bib; players: string[]; stars: number }
export interface DrawChecks {
  /** Chaque équipe a son meneur (ou un polyvalent) / son intérieur ; null : critère ignoré ou personne de ce poste. */
  meneur: boolean | null;
  interieur: boolean | null;
  young: boolean | null;
  mixite: boolean | null;
  apart: boolean;
  together: boolean;
}
export interface DrawResult {
  teams: DrawTeam[];
  /** L'écart en étoiles entre l'équipe la plus forte et la plus faible (à effectif égal). */
  gap: number;
  /** Les joueuses sans niveau (ni noté, ni donné pour ce tirage) : comptées « À l'aise ». */
  unrated: string[];
  checks: DrawChecks;
  /** Ce tirage est le même que celui d'avant (aucun autre aussi juste). */
  same: boolean;
}

/** Un hasard reproductible (mulberry32). */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffled<T>(xs: readonly T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}
/** La signature d'une répartition, sans l'ordre des équipes ni celui des joueuses. */
export const partitionKey = (teams: readonly (readonly string[])[]): string =>
  teams.map(t => [...t].sort().join(',')).sort().join('|');

// Les poids : un lien d'abord, puis la catégorie choisie, les postes, la mixité, enfin l'écart de niveau.
const W = { apart: 1000, together: 1000, poste: 30, young: 40, mix: 20, level: 12, age: 40 };

/** Le tirage. */
export function draw(req: DrawRequest): DrawResult {
  const seen = new Set<string>();
  const players = req.players.filter(p => (seen.has(p.id) ? false : (seen.add(p.id), true)));
  const n = players.length;
  const k = n ? Math.max(1, Math.min(req.teams, req.max ?? TEAMS_MAX, n)) : 1;
  const sizes = sizesFor(n, k);
  const crit = req.criteria;
  const temp = req.temp ?? {};
  const byId = new Map(players.map(p => [p.id, p]));
  const lvl = (id: string): number => temp[id] ?? byId.get(id)?.level ?? DEFAULT_LEVEL;
  const unrated = players.filter(p => p.level === null && !temp[p.id]).map(p => p.id);
  const mean = n ? players.reduce((s, p) => s + lvl(p.id), 0) / n : DEFAULT_LEVEL;
  const maxSize = Math.max(...sizes);

  // Les liens « ensemble » font des blocs (jamais plus grands qu'une équipe : au-delà, le lien ne tient pas).
  const parent = new Map(players.map(p => [p.id, p.id]));
  const root = (x: string): string => { let r = x; while (parent.get(r) !== r) r = parent.get(r)!; return r; };
  const size = (r: string) => players.filter(p => root(p.id) === r).length;
  for (const [a, b] of crit.together) {
    if (!byId.has(a) || !byId.has(b)) continue;
    const ra = root(a), rb = root(b);
    if (ra !== rb && size(ra) + size(rb) <= Math.min(...sizes)) parent.set(rb, ra);
  }
  const unitsMap = new Map<string, string[]>();
  for (const p of players) { const r = root(p.id); unitsMap.set(r, [...(unitsMap.get(r) ?? []), p.id]); }
  const units = [...unitsMap.values()];
  const unitLvl = (u: string[]) => u.reduce((s, id) => s + lvl(id), 0);

  // Ce qui compte, selon les critères.
  const able = (id: string, p: Poste) => { const x = byId.get(id)!.postes; return x.includes(p) || x.includes('P'); };
  const postesOn = crit.postes === 'spread' && players.some(p => p.postes.length);
  const capable = (p: Poste) => players.filter(x => able(x.id, p)).length;
  const needM = postesOn && capable('M') >= k, needI = postesOn && capable('I') >= k;
  const needE = postesOn && players.filter(x => x.postes.includes('E')).length >= k;
  const young = crit.category === 'young' ? youngestYear(players) : null;
  const youngCount = young === null ? 0 : players.filter(p => p.birthYear === young).length;
  const sexKnown = players.filter(p => p.sex).length;
  const mixOn = crit.mixite === 'balance' && sexKnown >= 2 && new Set(players.map(p => p.sex).filter(Boolean)).size === 2;
  const apartPairs = crit.apart.filter(([a, b]) => byId.has(a) && byId.has(b));
  const togetherPairs = crit.together.filter(([a, b]) => byId.has(a) && byId.has(b));

  const adjusted = (t: string[]) => t.reduce((s, id) => s + lvl(id), 0) + (maxSize - t.length) * mean;
  const evaluate = (ts: string[][]): { primary: number; secondary: number } => {
    let primary = 0, secondary = 0;
    if (crit.level === 'balance' && k > 1) {
      const adj = ts.map(adjusted);
      primary += W.level * (Math.max(...adj) - Math.min(...adj));
      const avg = adj.reduce((s, x) => s + x, 0) / adj.length;
      secondary += adj.reduce((s, x) => s + Math.abs(x - avg), 0);
    }
    if (postesOn) {
      for (const t of ts) {
        if (needM && !t.some(id => able(id, 'M'))) primary += W.poste;
        if (needI && !t.some(id => able(id, 'I'))) primary += W.poste;
        if (needE && !t.some(id => byId.get(id)!.postes.includes('E'))) primary += W.poste / 3;
      }
      for (const p of ['M', 'E', 'I'] as Poste[]) {
        const c = ts.map(t => t.filter(id => byId.get(id)!.postes.includes(p)).length);
        secondary += Math.max(...c) - Math.min(...c);
      }
    }
    if (young !== null) {
      const c = ts.map(t => t.filter(id => byId.get(id)!.birthYear === young).length);
      const lacking = c.filter(x => x === 0).length;
      primary += W.young * Math.max(0, lacking - Math.max(0, k - youngCount));
      secondary += Math.max(...c) - Math.min(...c);
    }
    if (crit.category === 'age') {
      for (const t of ts) {
        const ys = t.map(id => byId.get(id)!.birthYear).filter((y): y is number => typeof y === 'number');
        // L'écart à l'année du milieu de l'équipe : chaque joueuse d'une autre année compte (une pente pour les échanges).
        const mid = [...ys].sort((a, b) => a - b)[Math.floor(ys.length / 2)];
        if (mid !== undefined) primary += W.age * ys.reduce((s, y) => s + Math.abs(y - mid), 0);
      }
    }
    if (mixOn) {
      const c = ts.map(t => t.filter(id => byId.get(id)!.sex === 'F').length);
      primary += W.mix * Math.max(0, Math.max(...c) - Math.min(...c) - 1);
      secondary += Math.max(...c) - Math.min(...c);
    }
    for (const [a, b] of apartPairs) if (ts.some(t => t.includes(a) && t.includes(b))) primary += W.apart;
    for (const [a, b] of togetherPairs) if (!ts.some(t => t.includes(a) && t.includes(b))) primary += W.together;
    return { primary, secondary };
  };
  const score = (e: { primary: number; secondary: number }) => e.primary + e.secondary * 0.01;

  const rnd = seeded(req.seed);
  type Start = { ts: string[][]; unitOf: Map<string, string[]> };
  /** Un départ : les blocs d'abord (le plus grand dans l'équipe la moins remplie), puis le serpentin. Un bloc qui ne
   *  tient plus nulle part redevient des joueuses seules (son lien ne tiendra pas). */
  const start = (): Start => {
    const ts: string[][] = sizes.map(() => []);
    const unitOf = new Map<string, string[]>();
    const room = (i: number) => sizes[i]! - ts[i]!.length;
    const order = shuffled(units, rnd);
    const singles = order.filter(u => u.length === 1);
    for (const u of order.filter(u => u.length > 1).sort((a, b) => b.length - a.length)) {
      const i = shuffled(ts.map((_, j) => j), rnd).filter(j => room(j) >= u.length).sort((a, b) => room(b) - room(a))[0];
      if (i === undefined) { for (const id of u) singles.push([id]); continue; }
      ts[i]!.push(...u);
      for (const id of u) unitOf.set(id, u);
    }
    // Le serpentin, au hasard dans un même niveau (niveau ignoré : au hasard tout court).
    const list = crit.level === 'balance' ? [...singles].sort((a, b) => unitLvl(b) - unitLvl(a)) : singles;
    const teamOrder = shuffled(ts.map((_, j) => j), rnd);
    let pos = 0, dir = 1;
    const step = () => { pos += dir; if (pos >= k || pos < 0) { dir = -dir; pos += dir; } };
    for (const u of list) {
      for (let guard = 0; room(teamOrder[pos]!) <= 0 && guard < 2 * k; guard++) step();
      ts[teamOrder[pos]!]!.push(u[0]!);
      unitOf.set(u[0]!, u);
      step();
    }
    return { ts, unitOf };
  };
  /** Échanges deux à deux (blocs de même taille) tant que ça améliore. */
  const improve = ({ ts, unitOf }: Start) => {
    let cur = score(evaluate(ts));
    for (let pass = 0; pass < 30; pass++) {
      let better = false;
      for (let i = 0; i < k; i++) for (let j = i + 1; j < k; j++) {
        const ui = [...new Set(ts[i]!.map(id => unitOf.get(id)!))], uj = [...new Set(ts[j]!.map(id => unitOf.get(id)!))];
        for (const a of ui) for (const b of uj) {
          if (a.length !== b.length || !a.every(x => ts[i]!.includes(x)) || !b.every(x => ts[j]!.includes(x))) continue;
          const ti = ts[i]!, tj = ts[j]!;
          ts[i] = [...ti.filter(x => !a.includes(x)), ...b];
          ts[j] = [...tj.filter(x => !b.includes(x)), ...a];
          const s = score(evaluate(ts));
          if (s < cur - 1e-9) { cur = s; better = true; } else { ts[i] = ti; ts[j] = tj; }
        }
      }
      if (!better) break;
    }
    return ts;
  };

  const avoidKey = req.avoid ? partitionKey(req.avoid) : null;
  const tries: { ts: string[][]; primary: number; s: number; key: string }[] = [];
  const restarts = n <= 1 ? 1 : 24;
  for (let r = 0; r < restarts; r++) {
    const ts = improve(start());
    const e = evaluate(ts);
    tries.push({ ts, primary: e.primary, s: score(e), key: partitionKey(ts) });
  }
  const best = Math.min(...tries.map(t => t.primary));
  const fair = tries.filter(t => t.primary <= best + 1e-9);
  // Le plus juste ; « Relancer » : un autre aussi juste (même premier critère), pris au hasard parmi eux.
  const pool = avoidKey ? fair.filter(t => t.key !== avoidKey) : fair;
  const pick = pool.length ? pool[Math.floor(rnd() * pool.length)]! : fair.sort((a, b) => a.s - b.s)[0]!;
  // Les chasubles dans un ordre au hasard : la première (Rouges) ne dit rien du niveau ; dans une équipe, les plus fortes d'abord.
  const teams = shuffled(pick.ts, rnd).map((t, i) => ({ bib: BIBS[i % BIBS.length]!, players: [...t].sort((a, b) => lvl(b) - lvl(a)), stars: t.reduce((s, id) => s + lvl(id), 0) }));
  const adj = teams.map(t => adjusted(t.players));
  const ts = teams.map(t => t.players);
  return {
    teams,
    gap: k > 1 ? Math.round(Math.max(...adj) - Math.min(...adj)) : 0,
    unrated,
    checks: {
      meneur: needM ? ts.every(t => t.some(id => able(id, 'M'))) : null,
      interieur: needI ? ts.every(t => t.some(id => able(id, 'I'))) : null,
      young: young !== null && youngCount >= k ? ts.every(t => t.some(id => byId.get(id)!.birthYear === young)) : null,
      mixite: mixOn ? (() => { const c = ts.map(t => t.filter(id => byId.get(id)!.sex === 'F').length); return Math.max(...c) - Math.min(...c) <= 1; })() : null,
      apart: apartPairs.every(([a, b]) => !ts.some(t => t.includes(a) && t.includes(b))),
      together: togetherPairs.every(([a, b]) => ts.some(t => t.includes(a) && t.includes(b))),
    },
    same: !!avoidKey && pick.key === avoidKey,
  };
}

/** Le tirage verrouillé devient les groupes de l'exercice (D-052) : un groupe par équipe, sa chasuble, son nom. */
export function toGroups(r: DrawResult, nameOf: (bib: Bib) => string): ExoGroup[] {
  return r.teams.map(t => ({ name: nameOf(t.bib), bib: t.bib, players: [...t.players] }));
}

/** La phrase du tirage pour le coach : « Écart : 1 étoile. Chaque équipe a une meneuse (ou une polyvalente) et une
 *  intérieure ; Mia et Louna sont séparées. » ; `ok` faux quand un critère n'a pas pu tenir. */
export function drawSummary(r: DrawResult, c: DrawCriteria, g: TeamGender, nameOf: (id: string) => string, youngLabel: string | null): { head: string; text: string; ok: boolean } {
  const W1 = { meneur: agree(g, 'un meneur', 'une meneuse'), poly: agree(g, 'un polyvalent', 'une polyvalente'), interieur: agree(g, 'un intérieur', 'une intérieure') };
  const parts: string[] = [];
  let ok = true;
  const ch = r.checks;
  const m = ch.meneur ? `${W1.meneur} (ou ${W1.poly})` : null;
  const i = ch.interieur ? W1.interieur : null;
  if (m || i) parts.push(`chaque équipe a ${[m, i].filter(Boolean).join(' et ')}`);
  if (ch.meneur === false) { ok = false; parts.push(`une équipe sans ${agree(g, 'meneur', 'meneuse')}`); }
  if (ch.interieur === false) { ok = false; parts.push(`une équipe sans ${agree(g, 'intérieur', 'intérieure')}`); }
  if (ch.young === true && youngLabel) parts.push(`chaque équipe a ${agree(g, 'un', 'une')} ${youngLabel}`);
  if (ch.young === false) { ok = false; parts.push(`une équipe sans ${youngLabel ?? 'plus jeune'}`); }
  if (ch.mixite === true) parts.push('filles et garçons répartis');
  if (ch.mixite === false) { ok = false; parts.push('filles et garçons pas tout à fait répartis'); }
  const apart = c.apart.filter(([a, b]) => r.teams.some(t => t.players.includes(a)) && r.teams.some(t => t.players.includes(b)));
  const together = c.together.filter(([a, b]) => r.teams.some(t => t.players.includes(a)) && r.teams.some(t => t.players.includes(b)));
  const pair = ([a, b]: [string, string]) => `${nameOf(a)} et ${nameOf(b)}`;
  if (apart.length) parts.push(`${apart.map(pair).join(', ')} sont ${agree(g, 'séparés', 'séparées')}`);
  if (together.length) parts.push(`${together.map(pair).join(', ')} jouent ensemble`);
  if (!ch.apart || !ch.together) ok = false;
  const head = c.level === 'balance' ? `Écart : ${r.gap} étoile${r.gap > 1 ? 's' : ''}.` : 'Niveau ignoré.';
  if (c.level === 'balance' && r.gap > 1) ok = false;
  const text = parts.length ? `${parts[0]!.charAt(0).toUpperCase()}${parts[0]!.slice(1)}${parts.length > 1 ? ` ; ${parts.slice(1).join(' ; ')}` : ''}.` : '';
  return { head, text, ok };
}
